import { AppError, err, ok, type Result } from '@aibi/shared';
import {
  parseWorkspace,
  serializeWorkspace,
  type WorkspaceDocument,
} from '../model/workspace-document';

/**
 * Puerto de persistencia de workspaces. Hoy guarda un borrador local; la persistencia
 * en servidor (con revisiones y concurrencia optimista) llegara con el backend.
 */
export interface WorkspaceRepository {
  load(workspaceId: string): Promise<Result<WorkspaceDocument | null, AppError>>;
  save(document: WorkspaceDocument): Promise<Result<void, AppError>>;
}

/** Subconjunto de `Storage` que necesita el repositorio (inyectable en pruebas). */
export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem'>;

const STORAGE_PREFIX = 'aibi.workspace.';

/** Borrador local en `localStorage`; el documento se valida al leerlo. */
export function createLocalWorkspaceRepository(storage: KeyValueStorage): WorkspaceRepository {
  return {
    load: (workspaceId) => {
      try {
        const text = storage.getItem(`${STORAGE_PREFIX}${workspaceId}`);
        return Promise.resolve(text === null ? ok(null) : parseWorkspace(text));
      } catch (cause) {
        return Promise.resolve(
          err(new AppError('UNAVAILABLE', 'No se pudo leer el borrador local.', { cause })),
        );
      }
    },
    save: (document) => {
      try {
        storage.setItem(`${STORAGE_PREFIX}${document.id}`, serializeWorkspace(document));
        return Promise.resolve(ok(undefined));
      } catch (cause) {
        return Promise.resolve(
          err(new AppError('UNAVAILABLE', 'No se pudo guardar el borrador local.', { cause })),
        );
      }
    },
  };
}

/** Repositorio en memoria (pruebas y entornos sin almacenamiento). */
export function createMemoryWorkspaceRepository(): WorkspaceRepository {
  const store = new Map<string, string>();
  return createLocalWorkspaceRepository({
    getItem: (key) => store.get(key) ?? null,
    setItem: (key, value) => {
      store.set(key, value);
    },
  });
}
